import { type PropsWithChildren } from 'react';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { motion } from 'framer-motion';
import { darken, lighten } from 'polished';
import styled from 'styled-components';

import {
  useNavDropdownContext,
  useNavDropdownTransform
} from './NavDropdownProvider';

const NavigationMenuViewport = styled(NavigationMenu.Viewport)`
  position: absolute;
  inset: 0;
`;

const NavigationMenuViewportPosition = styled.div`
  will-change: transform, height, width;
  position: absolute;
  border-radius: calc(5rem / 16);
  overflow: clip;

  box-shadow: ${({ theme }) => {
    const lowlight = `0px 0.5px 0 0.75px ${lighten(0.125, theme.palette.fg100)}`;
    const primary = '4px 16px 32px -12px hsla(0 0% 0% / 0.72)';
    return [lowlight, primary].join(',');
  }};

  &::before {
    pointer-events: none;
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }

  &::before {
    opacity: 0.98;
    background-image: ${({ theme }) => {
      const stop1 = theme.palette.fg100;
      const stop2 = darken(0.03125, theme.palette.fg100);
      return `radial-gradient(at 0% 0%, ${stop1} 58%, ${stop2})`;
    }};
  }

  [data-theme='pink'] & {
    box-shadow: ${() => {
      const lowlight =
        '0px 0.5px 0px 0.75px #707099, 0px 0.75px 0px 1px #35354A';
      const primary =
        '35px 83px 25px 0px rgba(100, 63, 63, 0.01), 22px 53px 23px 0px rgba(100, 63, 63, 0.06), 13px 30px 19px 0px rgba(100, 63, 63, 0.19), 6px 13px 14px 0px rgba(100, 63, 63, 0.32), 1px 3px 8px 0px rgba(100, 63, 63, 0.37)';
      return [lowlight, primary].join(',');
    }};

    &::before {
      opacity: 0.99;
      background-image: radial-gradient(
        132.18% 132.18% at 50% 132.04%,
        #14141c 0%,
        #222230 100%
      );
    }
  }
`;

const ViewportPosition = styled.div`
  position: absolute;
  width: 100%;
  top: calc(100% - 0.375rem);
  left: 0;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

function NavMenuViewportWrapper({ children }: PropsWithChildren) {
  const state = useNavDropdownContext();
  const transformRaw = useNavDropdownTransform();

  const transform = transformRaw ? `translateX(${transformRaw}px)` : 'none';

  const optOutOfTransitionProps = state.shouldAnimateSize
    ? {}
    : { transition: { duration: 0 } };

  return (
    <ViewportPosition
      animate={state.value ? 'visible' : 'hidden'}
      as={motion.div}
      initial="hidden"
      variants={{
        visible: {
          opacity: 1,
          transform: 'translateY(0rem) '
          // transition: { type: 'spring', bounce: 0, duration: 0.28 }
        },
        hidden: {
          opacity: 0,
          transform: 'translateY(-0.5rem)'
          // transition: { type: 'spring', bounce: 0, duration: 0.2 }
        }
      }}>
      <NavigationMenuViewportPosition
        animate={{
          transform,
          height: state.boundingBoxes?.content.height || 0,
          width: state.boundingBoxes?.content.width || 0
        }}
        as={motion.div}
        style={{
          pointerEvents: state.value ? 'auto' : 'none'
        }}
        {...optOutOfTransitionProps}>
        {children}
      </NavigationMenuViewportPosition>
    </ViewportPosition>
  );
}

export function NavMenuViewport() {
  return (
    <NavMenuViewportWrapper>
      <NavigationMenuViewport forceMount />
    </NavMenuViewportWrapper>
  );
}
